import { newDate } from "date-fns-jalali/newDate";
import { differenceInDays } from "date-fns";

/**
 * Formats a date using the "fa-IR" locale with a specific configuration.
 * @param date The date to format.
 * @param withHours show hours
 * @returns A formatted date string.
 */
export function toJalaliString(date: Date, withHours = true): string {
	return new Intl.DateTimeFormat("fa-IR", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: withHours ? "2-digit" : undefined,
		minute: withHours ? "2-digit" : undefined,
		hourCycle: withHours ? "h23" : undefined,
	}).format(date);
}
export const formatDate = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	// const hours = String(date.getHours()).padStart(2, "0");
	// const minutes = String(date.getMinutes()).padStart(2, "0");
	// const seconds = String(date.getSeconds()).padStart(2, "0");
	// return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

	return `${year}/${month}/${day}`;
};

export const formatTime = (seconds: number) => {
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;
	return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export const formatNumberDelimiter = (num: string | number): string => {
	// Convert the number to a string
	const numStr = num.toString();

	// Reverse the string to work from right to left
	const reversed = numStr.split("").reverse().join("");

	// Split into groups of three characters
	const groups = reversed.match(/.{1,3}/g) || [];

	// Join the groups with commas
	const formatted = groups.join(",");

	// Reverse back to the original order
	return formatted.split("").reverse().join("");
};

export function stringToBuffer(str: string): Uint8Array {
	return new TextEncoder().encode(str);
}

export function bufferToString(str: Uint8Array): string {
	return new TextDecoder().decode(str);
}

export function formatBytes(bytes: number) {
	const units = ["B", "KB", "MB"];
	let size = bytes;
	let unitIndex = 0;

	while (size >= 1024 && unitIndex < units.length - 1) {
		size /= 1024;
		unitIndex++;
	}
	return `${size.toFixed(1)}${units[unitIndex]}`;
}

export function extractTime(date: Date): {
	hours: number;
	minutes: number;
	seconds: number;
} {
	return {
		hours: date.getHours(),
		minutes: date.getMinutes(),
		seconds: date.getSeconds(),
	};
}
export function compareTimes(
	time1: { hours: number; minutes: number; seconds: number },
	time2: { hours: number; minutes: number; seconds: number },
): boolean {
	if (time1.hours > time2.hours) {
		return true; // time1 is later
	} else if (time1.hours < time2.hours) {
		return false; // time1 is earlier
	} else {
		if (time1.minutes > time2.minutes) {
			return true; // time1 is later
		} else if (time1.minutes < time2.minutes) {
			return false; // time1 is earlier
		} else {
			if (time1.seconds > time2.seconds) {
				return true; // time1 is later
			} else {
				return false; // time1 is earlier or equal
			}
		}
	}
}

export function isTimeOutStartEnd(start: string, end: string): boolean {
	const [startHours, startMinutes] = start.split(":").map(Number);
	const [endHours, endMinutes] = end.split(":").map(Number);

	const startTime: Date = new Date();
	const endTime: Date = new Date();
	const currentTime: Date = new Date();
	startTime.setHours(startHours, startMinutes, 0);
	endTime.setHours(endHours, endMinutes, 0);

	const startTimeComponents = extractTime(startTime);
	const endTimeComponents = extractTime(endTime);
	const currentTimeComponents = extractTime(currentTime);
	const isStartTimeLater = compareTimes(
		startTimeComponents,
		currentTimeComponents,
	);
	const isEndTimeLater = compareTimes(
		endTimeComponents,
		currentTimeComponents,
	);
	// console.log("isStartTimeLater:", isStartTimeLater);
	// console.log("isEndTimeLater:", isEndTimeLater);
	return !(!isStartTimeLater && isEndTimeLater);
}

export const toFarsiNumber = (
	num: number | bigint | string | undefined | null,
	currency: boolean = false,
) => {
	if (num === undefined) return undefined;
	if (num === null) return null;
	const farsiDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
	if (currency)
		num = num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
	else num = num.toString();
	return num
		.split("")
		.map((digit) => farsiDigits[digit as unknown as number] || digit)
		.join("");
};

export const getDaysBetweenDates = (date1: Date, date2: Date) => {
	return Math.abs(differenceInDays(new Date(date1), new Date(date2)));
};

export function jalaliToGregorianYear(year: number): number | number[] {
	const first = newDate(year, 0, 1);
	const last = newDate(year, 11, 29);
	if (first.getFullYear() === last.getFullYear()) return first.getFullYear();
	else return [first.getFullYear(), last.getFullYear()];
}
